import React from "react";
import { motion } from "framer-motion";
import { content } from "../lib/i18n";
import { useLang } from "../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.08 },
  }),
};

const WhyChooseUs = () => {
  const { lang } = useLang();
  const fr = content.why.fr;
  const en = content.why.en;
  const isBoth = lang === "both";
  const data = lang === "en" ? en : fr;

  return (
    <section id="why" data-testid="why-section" className="bg-white section">
      <div className="mx-auto max-w-[1320px] px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <span className="eyebrow-yellow">
              <span>★</span>
              <span>{isBoth ? `${fr.eyebrow} · ${en.eyebrow}` : data.eyebrow}</span>
            </span>
            <h2
              data-testid="why-title"
              className="mt-5 h-display text-black text-[36px] sm:text-[44px] md:text-[60px] leading-[0.95]"
            >
              <span className="block">{data.title}</span>
              <span className="block">
                <span className="inline-block italic mt-1" style={{
                  background: "linear-gradient(transparent 60%, var(--bb-yellow) 60%)",
                  padding: "0 0.15em",
                }}>
                  {data.titleAccent}
                </span>
              </span>
            </h2>
          </div>
          <p className="text-base text-black/70 max-w-sm">
            {isBoth ? `${fr.sub} · ${en.sub}` : data.sub}
          </p>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-5"
          data-testid="why-grid"
        >
          {data.items.map((item, i) => {
            const altItem = isBoth ? en.items[i] : null;
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                data-testid={`why-item-${i}`}
                className="why-box group"
              >
                <div className="text-[40px] md:text-[48px] leading-none mb-6">
                  {item.icon}
                </div>
                <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-black/55 mb-2">
                  0{i + 1}
                </div>
                <h3 className="font-display text-xl md:text-[24px] leading-tight uppercase text-black">
                  {isBoth ? fr.items[i].t : item.t}
                </h3>
                {isBoth && altItem && (
                  <h4 className="mt-1 font-body italic text-base text-black/65">
                    {altItem.t}
                  </h4>
                )}
                <p className="mt-3 text-[14px] text-black/70 leading-relaxed">
                  {isBoth ? fr.items[i].d : item.d}
                </p>
                {isBoth && altItem && (
                  <p className="mt-1 text-[13px] italic text-black/50 leading-relaxed">
                    {altItem.d}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
